/**
 * Feature registry. Each feature folder's index.ts default-exports its
 * manifest; they are imported lazily, which buys two things:
 *  - a production build emits them as separate chunks that are never fetched
 *    (the kit is inert without VITE_DEMO_MODE), and
 *  - a feature that fails to load is skipped (and flagged in the Sim Panel)
 *    instead of taking the whole app down.
 */
import { useSyncExternalStore } from "react";
import { isFeatureEnabled, SIM_ACTIVE, SIM_FEATURE_IDS, type SimFeatureId } from "./flags";
import { consumeBootParams, getSimState, subscribeSim, useSimStore, type SimState } from "./store";
import { SIM_PARAMS } from "./params";
import type { FeatureManifest, SlotName } from "./types";

const MODULES = import.meta.glob<{ default: FeatureManifest }>("../*/index.ts");

const loaded = new Map<SimFeatureId, FeatureManifest>();
const failures = new Map<SimFeatureId, string>();
const pending = new Map<SimFeatureId, Promise<void>>();
const listeners = new Set<() => void>();
let version = 0;
let ready = !SIM_ACTIVE;
let boot: Promise<void> | null = null;

function bump(): void {
  version++;
  listeners.forEach((listener) => listener());
}

export function subscribeRegistry(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getRegistryVersion(): number {
  return version;
}

export function ensureFeatureLoaded(id: SimFeatureId): Promise<void> {
  const existing = pending.get(id);
  if (existing) return existing;
  const loader = MODULES[`../${id}/index.ts`];
  const promise = (loader ? loader() : Promise.reject(new Error(`no ${id}/index.ts`)))
    .then((mod) => {
      loaded.set(id, mod.default);
    })
    .catch((err: unknown) => {
      failures.set(id, err instanceof Error ? err.message : String(err));
    })
    .finally(bump);
  pending.set(id, promise);
  return promise;
}

/**
 * Enabled and present. A switch can be on for a feature whose folder has not
 * been built yet; that is skipped quietly rather than reported as a failure.
 */
function isLoadable(id: SimFeatureId): boolean {
  return isFeatureEnabled(id) && MODULES[`../${id}/index.ts`] !== undefined;
}

/** Runs ?state=<name> presets once every boot-time feature has loaded. */
function runPresets(): void {
  const names = (SIM_PARAMS.get("state") ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  for (const name of names) {
    for (const manifest of loaded.values()) manifest.presets?.[name]?.();
  }
  consumeBootParams("state");
}

/** Loads every enabled feature (once). The mock router awaits this before answering. */
export function bootFeatures(): Promise<void> {
  if (!boot) {
    boot = SIM_ACTIVE
      ? Promise.all(SIM_FEATURE_IDS.filter(isLoadable).map(ensureFeatureLoaded)).then(
          () => {
            runPresets();
            ready = true;
            bump();
          },
        )
      : Promise.resolve();
  }
  return boot;
}

if (SIM_ACTIVE) {
  void bootFeatures();
  // A feature switched on in the Sim Panel loads the moment it is enabled.
  subscribeSim(() => {
    for (const id of SIM_FEATURE_IDS) {
      if (isLoadable(id) && !pending.has(id)) void ensureFeatureLoaded(id);
    }
  });
}

export function getLoadedManifest(id: SimFeatureId): FeatureManifest | undefined {
  return loaded.get(id);
}

export function getFeatureFailures(): ReadonlyMap<SimFeatureId, string> {
  return failures;
}

/** Enabled AND loaded manifests, in SIM_FEATURE_IDS order. */
export function getEnabledManifests(state: SimState = getSimState()): FeatureManifest[] {
  return SIM_FEATURE_IDS.filter((id) => isFeatureEnabled(id, state))
    .map((id) => loaded.get(id))
    .filter((m): m is FeatureManifest => m !== undefined);
}

export function useSimReady(): boolean {
  useSyncExternalStore(subscribeRegistry, getRegistryVersion, getRegistryVersion);
  return ready;
}

/** Reactive getEnabledManifests (re-renders on load and on Sim Panel toggles). */
export function useEnabledManifests(): FeatureManifest[] {
  useSyncExternalStore(subscribeRegistry, getRegistryVersion, getRegistryVersion);
  useSimStore((s) => s.featureOverrides);
  return getEnabledManifests();
}

/**
 * Whether any enabled feature registers into `name`, so a page can leave out
 * the heading over a slot that would render nothing (the dashboard's "More for
 * you"). An entry can still render null on its own; pair this with
 * `empty:hidden`-style CSS for that case.
 */
export function useSlotHasEntries(name: SlotName): boolean {
  const manifests = useEnabledManifests();
  return SIM_ACTIVE && manifests.some((m) => (m.slots?.[name]?.length ?? 0) > 0);
}
